import { Header, Icon, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { useTheme, useNavigation } from '@react-navigation/native'
import { Theme } from '@theme'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { View, StyleSheet, Alert } from 'react-native'
import jsQR from 'jsqr'

const KEEPTOUCH_PROFILE_PATTERN = /\/profile\/([a-zA-Z0-9-]+)/

export function QRCodeScanner() {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getScanStyles(theme)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleScannedUrl = useCallback((url: string) => {
    if (isProcessing) return
    setIsProcessing(true)

    const match = url.match(KEEPTOUCH_PROFILE_PATTERN)
    if (match) {
      const profileId = match[1]
      stopCamera()
      ;(navigation as any).navigate('Profile', {
        profileId,
        isContact: false,
      })
    } else {
      Alert.alert(
        t('qrCode.info'),
        t('qrCode.notKeepTouchUrl'),
        [
          {
            text: t('form.back'),
            onPress: () => {
              setIsProcessing(false)
            },
          },
        ],
      )
    }
  }, [isProcessing, navigation, t])

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
  }, [])

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
        setCameraActive(true)
      }
    } catch (error) {
      setPermissionDenied(true)
    }
  }, [])

  const scanWithJsQR = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const imageData = ctx.getImageData(0, 0, width, height)
    const result = jsQR(imageData.data, width, height)
    if (result && result.data) {
      handleScannedUrl(result.data)
    }
  }, [handleScannedUrl])

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || isProcessing) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Use BarcodeDetector API if available, otherwise fall back to jsQR
    if ('BarcodeDetector' in window) {
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] })
      detector.detect(canvas).then((barcodes: any[]) => {
        if (barcodes.length > 0 && barcodes[0].rawValue) {
          handleScannedUrl(barcodes[0].rawValue)
        }
      }).catch(() => {
        // BarcodeDetector failed, try jsQR as fallback
        scanWithJsQR(ctx, canvas.width, canvas.height)
      })
    } else {
      scanWithJsQR(ctx, canvas.width, canvas.height)
    }
  }, [handleScannedUrl, isProcessing, scanWithJsQR])

  useEffect(() => {
    startCamera()
    return () => {
      stopCamera()
    }
  }, [])

  useEffect(() => {
    if (cameraActive && !isProcessing) {
      scanIntervalRef.current = setInterval(scanFrame, 500)
    }
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current)
        scanIntervalRef.current = null
      }
    }
  }, [cameraActive, isProcessing, scanFrame])

  return (
    <Screen>
      <Header
        iconLeft="arrowBack"
        onPressLeft={() => {
          stopCamera()
          navigation.goBack()
        }}
        title={t('qrCode.scanTitle')}
      />

      <View style={styles.container}>
        {permissionDenied ? (
          <View style={styles.permissionContainer}>
            <Icon name="scan" size={100} color={theme.colors.greyBlue} />
            <Text type="bodyBold" color={theme.colors.primary} style={styles.permissionTitle}>
              {t('qrCode.cameraPermissionTitle')}
            </Text>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.permissionMessage}>
              {t('qrCode.cameraPermissionMessage')}
            </Text>
          </View>
        ) : (
          <View style={styles.cameraContainer}>
            {/* @ts-ignore - web-only elements */}
            <video
              ref={videoRef}
              style={{
                width: '100%',
                maxWidth: 500,
                borderRadius: 16,
                objectFit: 'cover',
              }}
              autoPlay
              playsInline
              muted
            />
            {/* Hidden canvas for frame processing */}
            {/* @ts-ignore - web-only elements */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.instructionText}>
              {t('qrCode.scanInstruction')}
            </Text>
            {!('BarcodeDetector' in window) && (
              <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.fallbackText}>
                {t('qrCode.scanWebFallbackActive')}
              </Text>
            )}
          </View>
        )}
      </View>
    </Screen>
  )
}

const getScanStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    permissionContainer: {
      alignItems: 'center',
      paddingHorizontal: theme.spacing.xl,
    },
    permissionTitle: {
      marginTop: theme.spacing.lg,
      textAlign: 'center',
    },
    permissionMessage: {
      marginTop: theme.spacing.sm,
      textAlign: 'center',
      lineHeight: 20,
    },
    cameraContainer: {
      alignItems: 'center',
      width: '100%',
    },
    instructionText: {
      marginTop: theme.spacing.lg,
      textAlign: 'center',
    },
    fallbackText: {
      marginTop: theme.spacing.sm,
      textAlign: 'center',
      fontStyle: 'italic',
    },
  })
